import * as React from "react";
import { FiStar } from "react-icons/fi";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

// StatCard applying Ceylanthro styling
const StatCard = ({ value, label }) => (
  <div className="flex flex-col border-l-2 border-brand-darkSage/20 pl-4 md:pl-6">
    <span className="text-3xl md:text-5xl font-serif text-brand-darkSage mb-2">{value}</span>
    <span className="text-xs text-brand-darkSage/70 uppercase tracking-widest font-semibold">{label}</span>
  </div>
);

// A sticky testimonial card for the stacking effect
const StickyTestimonialCard = ({ testimonial, index, total }) => {
  return (
    <Card 
      className="sticky flex flex-col justify-between bg-white/60 backdrop-blur-xl border-white shadow-[0_20px_40px_-15px_rgba(0,0,0,0.05)] rounded-3xl overflow-hidden"
      style={{
        top: `calc(15vh + ${index * 30}px)`, // Stagger the stacking
        height: "420px",
        marginBottom: index === total - 1 ? "0" : "40px"
      }}
    >
      <CardContent className="p-8 md:p-10 flex flex-col h-full justify-between">
        {/* Top section: Image and Author */}
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-brand-sand shadow-sm">
            <img src={testimonial.avatarSrc} alt={testimonial.name} className="w-full h-full object-cover" />
          </div>
          <div>
            <h3 className="font-serif text-xl text-brand-darkSage font-bold">{testimonial.name}</h3>
            <p className="text-xs text-brand-lightText uppercase tracking-wider mt-1">{testimonial.title}</p>
          </div>
        </div>

        {/* Middle section: Rating */}
        <div className="flex items-center gap-1 my-6 text-brand-gold">
          <span className="text-sm font-bold text-brand-darkSage mr-2">{testimonial.rating.toFixed(1)}</span>
          {Array.from({ length: 5 }).map((_, i) => (
            <FiStar 
              key={i} 
              className={cn(
                "w-4 h-4",
                i < Math.floor(testimonial.rating) ? "fill-current text-brand-gold" : "text-brand-darkSage/20"
              )} 
            />
          ))}
        </div>

        {/* Bottom section: Quote */}
        {testimonial.quote && (
          <blockquote className="text-lg md:text-xl font-serif text-brand-darkSage/90 italic leading-relaxed">
            "{testimonial.quote}"
          </blockquote>
        )}
      </CardContent>
    </Card>
  );
};

export const ClientsSection = ({
  tagLabel,
  title,
  description,
  stats,
  testimonials,
  primaryActionLabel,
  onPrimaryAction,
  secondaryActionLabel,
  onSecondaryAction,
  className,
}) => {
  return (
    <section className={cn("py-24 px-6 md:px-12 lg:px-24 bg-[#e8e4d9] relative", className)}>
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 relative">
        
        {/* Left Column: Sticky Content */}
        <div className="flex flex-col justify-start lg:sticky lg:top-[15vh] h-fit">
          <p className="text-brand-darkSage tracking-[0.2em] font-semibold text-xs mb-4 uppercase">
            {tagLabel}
          </p>
          <h2 className="text-4xl md:text-6xl font-serif text-brand-darkSage mb-6 leading-tight">
            {title.split(' ').map((word, i) => (
              <React.Fragment key={i}>
                {word} {i === 1 && <br/>}
              </React.Fragment>
            ))}
          </h2>
          <p className="text-brand-lightText text-base md:text-lg mb-12 max-w-md leading-relaxed">
            {description}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-8 sm:gap-12 mb-12">
            {stats.map((stat, i) => (
              <StatCard key={i} {...stat} />
            ))}
          </div>

          <div className="flex flex-wrap gap-4 mt-auto">
            <Button 
              onClick={onPrimaryAction}
              className="bg-brand-darkSage hover:bg-brand-gold text-white rounded-full px-8 py-6 h-auto text-xs tracking-widest uppercase font-bold transition-all shadow-lg border-none"
            >
              {primaryActionLabel}
            </Button>
            <Button 
              onClick={onSecondaryAction}
              variant="outline" 
              className="border-brand-darkSage text-brand-darkSage hover:bg-brand-sand rounded-full px-8 py-6 h-auto text-xs tracking-widest uppercase font-bold transition-all"
            >
              {secondaryActionLabel}
            </Button>
          </div>
        </div>

        {/* Right Column: Stacked Cards Container */}
        <div className="relative w-full pb-[10vh]">
          <div className="flex flex-col gap-6 w-full">
            {testimonials.map((testimonial, index) => (
              <StickyTestimonialCard 
                key={index} 
                testimonial={testimonial} 
                index={index} 
                total={testimonials.length} 
              />
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};